import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoIcon from '@mui/icons-material/Info';
import ClearIcon from '@mui/icons-material/Clear';
import { getAvailableTimeSlots, getDayName, getWeekdayKey } from '../../services/fortuneTellerService';

// 样式化组件
const TimeSlotButton = styled(Button)(({ theme, selected, disabled }) => ({
  borderColor: selected 
    ? theme.palette.primary.main 
    : disabled 
      ? theme.palette.grey[200] 
      : theme.palette.grey[300],
  color: selected 
    ? theme.palette.primary.main 
    : disabled 
      ? theme.palette.grey[400] 
      : theme.palette.text.primary,
  backgroundColor: selected 
    ? 'rgba(156, 39, 176, 0.1)' 
    : 'transparent',
  '&:hover': {
    backgroundColor: selected 
      ? 'rgba(156, 39, 176, 0.15)' 
      : 'rgba(0, 0, 0, 0.04)'
  },
  fontWeight: selected ? 'bold' : 'normal',
  minWidth: '90px',
  margin: theme.spacing(0.5),
  '&.Mui-disabled': {
    color: theme.palette.grey[400],
    backgroundColor: theme.palette.grey[100],
    borderColor: theme.palette.grey[200],
  }
}));

const SelectedTimeChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: 'rgba(156, 39, 176, 0.1)',
  '& .MuiChip-label': {
    color: theme.palette.primary.main
  }
}));

const CalendarPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  borderRadius: theme.shape.borderRadius,
}));

const TimeSlotSelector = ({ 
  fortuneTellerId, 
  onTimeSlotSelect, 
  initialDate = null,
  initialSelectedTimeSlots = [],
  maxTimeSlots = 4
}) => {
  const [selectedDate, setSelectedDate] = useState(initialDate || new Date());
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState(initialSelectedTimeSlots || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // 当日期变化时获取可用时间段
  useEffect(() => {
    fetchAvailableTimeSlots();
  }, [selectedDate, fortuneTellerId]);
  
  // 获取可用时间段
  const fetchAvailableTimeSlots = async () => {
    if (!fortuneTellerId || !selectedDate) {
      console.log('缺少必要参数，无法获取时间段', { fortuneTellerId, selectedDate });
      return;
    }
    
    console.log(`开始获取占卜师${fortuneTellerId}在${selectedDate.toLocaleDateString()}的可用时间段`);
    setLoading(true);
    setError('');
    
    try {
      // 检查日期是否有效
      if (isNaN(selectedDate.getTime())) {
        throw new Error('日期无效');
      }
      
      const timeSlots = await getAvailableTimeSlots(fortuneTellerId, selectedDate);
      console.log(`获取到${timeSlots.length}个可用时间段:`, timeSlots);
      
      // 确保时间段是字符串数组
      const validTimeSlots = Array.isArray(timeSlots) 
        ? timeSlots.filter(slot => typeof slot === 'string') 
        : [];
        
      if (validTimeSlots.length !== timeSlots.length) {
        console.warn('过滤掉了部分无效的时间段');
      }
      
      // 更新可用时间段
      setAvailableTimeSlots(validTimeSlots);
      
      // 检查当前选择的时间段是否仍有效
      const validSelectedSlots = selectedTimeSlots.filter(slot => 
        validTimeSlots.includes(slot)
      );
      
      // 如果选择的时间段有变化，更新状态并通知父组件
      if (validSelectedSlots.length !== selectedTimeSlots.length) {
        console.log('部分已选时间段不再可用，已移除');
        setSelectedTimeSlots(validSelectedSlots);
      }
    } catch (error) {
      console.error('获取时间段失败:', error);
      console.error('错误详情:', error.code, error.message, error.stack);
      setError(`获取可用时间段失败: ${error.message || '请稍后重试'}`);
      
      // 设置默认时间段用于界面展示
      console.log('使用默认时间段用于展示');
      setAvailableTimeSlots(['10:00', '10:30', '11:00', '11:30', '14:00', '14:30', '15:00', '15:30']);
    } finally {
      setLoading(false);
    }
  };
  
  // 处理时间段选择
  const handleTimeSlotClick = (timeSlot) => {
    // 检查是否已选择
    if (selectedTimeSlots.includes(timeSlot)) {
      // 移除已选择的时间段
      setSelectedTimeSlots(
        selectedTimeSlots.filter(slot => slot !== timeSlot)
      );
    } else {
      // 检查是否已达到最大选择数量
      if (selectedTimeSlots.length >= maxTimeSlots) {
        return;
      }
      
      // 添加新的时间段 (并排序)
      const newSelectedTimeSlots = [...selectedTimeSlots, timeSlot].sort();
      setSelectedTimeSlots(newSelectedTimeSlots);
    }
  };
  
  // 清除所有选择的时间段
  const clearSelectedTimeSlots = () => {
    setSelectedTimeSlots([]);
  };
  
  // 删除单个时间段
  const removeTimeSlot = (timeSlot) => {
    setSelectedTimeSlots(
      selectedTimeSlots.filter(slot => slot !== timeSlot)
    );
  };
  
  // 当选择的时间段变化时通知父组件
  useEffect(() => {
    if (onTimeSlotSelect) {
      onTimeSlotSelect(selectedTimeSlots, selectedDate);
    }
  }, [selectedTimeSlots, selectedDate]);
  
  // 是否可以选择某个日期 (限制为未来30天内)
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30);
    
    return date < today || date > maxDate;
  };
  
  // 格式化时间段展示
  const formatTimeSlot = (slot) => {
    return slot;
  };
  
  // 组合时间段展示（如果连续时间段选择）
  const getCombinedTimeSlots = () => {
    if (selectedTimeSlots.length === 0) return [];
    
    const result = [];
    let currentGroup = [selectedTimeSlots[0]];
    
    for (let i = 1; i < selectedTimeSlots.length; i++) {
      const prevTime = selectedTimeSlots[i - 1];
      const currTime = selectedTimeSlots[i];
      
      // 检查是否连续时间段 (假设时间格式为 "HH:MM")
      const prevHour = parseInt(prevTime.split(':')[0]);
      const prevMin = parseInt(prevTime.split(':')[1]);
      const currHour = parseInt(currTime.split(':')[0]);
      const currMin = parseInt(currTime.split(':')[1]);
      
      // 检查是否连续 (假设时间间隔为30分钟)
      const prevTimeInMinutes = prevHour * 60 + prevMin;
      const currTimeInMinutes = currHour * 60 + currMin;
      
      if (currTimeInMinutes - prevTimeInMinutes === 30) {
        // 连续时间段，添加到当前分组
        currentGroup.push(currTime);
      } else {
        // 不连续，创建新分组
        result.push([...currentGroup]);
        currentGroup = [currTime];
      }
    }
    
    // 添加最后一个分组
    result.push(currentGroup);
    
    return result;
  };
  
  return (
    <Box>
      <CalendarPaper>
        <Typography variant="h6" gutterBottom>
          选择预约日期和时间
        </Typography>
        
        {/* 日期选择器 */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="预约日期"
            value={selectedDate}
            onChange={(newDate) => setSelectedDate(newDate)}
            shouldDisableDate={isDateDisabled}
            renderInput={(params) => 
              <div style={{ width: '100%', marginTop: '16px', marginBottom: '16px' }}>
                {params.element}
              </div>
            }
            minDate={new Date()}
            maxDate={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)} // 30天内
          />
        </LocalizationProvider>
        
        <Divider sx={{ my: 2 }} />
        
        {/* 已选择的时间段 */}
        {selectedTimeSlots.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1">
                已选择的时间段 ({getDayName(getWeekdayKey(selectedDate))})
              </Typography>
              <Tooltip title="清除所有选择的时间段">
                <IconButton 
                  size="small" 
                  onClick={clearSelectedTimeSlots}
                  aria-label="清除所有选择的时间段"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
              {getCombinedTimeSlots().map((group, index) => {
                if (group.length === 1) {
                  // 单个时间段
                  return (
                    <SelectedTimeChip
                      key={group[0]}
                      label={formatTimeSlot(group[0])}
                      onDelete={() => removeTimeSlot(group[0])}
                    />
                  );
                } else {
                  // 连续时间段分组
                  return (
                    <SelectedTimeChip
                      key={index}
                      label={`${formatTimeSlot(group[0])} - ${formatTimeSlot(group[group.length - 1])}`}
                      onDelete={() => {
                        // 删除整个分组
                        group.forEach(slot => removeTimeSlot(slot));
                      }}
                    />
                  );
                }
              })}
            </Box>
            
            {selectedTimeSlots.length >= maxTimeSlots && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                已达到最大可选时间段数量 ({maxTimeSlots})
              </Typography>
            )}
          </Box>
        )}
        
        {/* 时间段选择 */}
        <Typography variant="subtitle1" gutterBottom>
          可用时间段 ({getDayName(getWeekdayKey(selectedDate))})
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        ) : availableTimeSlots.length === 0 ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            该日没有可用的预约时间段
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1 }}>
            {availableTimeSlots.map(timeSlot => (
              <TimeSlotButton
                key={timeSlot}
                variant="outlined"
                selected={selectedTimeSlots.includes(timeSlot)}
                onClick={() => handleTimeSlotClick(timeSlot)}
                disabled={
                  !selectedTimeSlots.includes(timeSlot) && 
                  selectedTimeSlots.length >= maxTimeSlots
                }
                startIcon={<AccessTimeIcon />}
              >
                {timeSlot}
              </TimeSlotButton>
            ))}
          </Box>
        )}
        
        {/* 说明提示 */}
        <Box sx={{ mt: 2, bgcolor: 'rgba(0,0,0,0.02)', p: 1, borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon fontSize="small" sx={{ mr: 0.5 }} />
            每个时间段为30分钟，最多可选择{maxTimeSlots}个时间段。选择连续的时间段可获得更长的咨询时间。
          </Typography>
        </Box>
      </CalendarPaper>
    </Box>
  );
};

export default TimeSlotSelector;